// apps/client/src/components/reports/LoanBookScopeToggle.tsx
//
// Entity scope toggle for the loan book panel. Phase 3 of
// docs/handovers/2026-08-04-LOAN-BOOK-VS-LMG-RECONCILIATION.md.
//
// "Primary loan book" names its entity explicitly, because Loan Market issues
// one report per entity and a comparison is meaningless without the entity
// named. When no primary entity is configured the control does NOT guess one:
// it locks to "All loan books" and says so.

import React from 'react';
import type { LoanBookEntity, LoanBookMode } from '../../types/loanBookView';

export interface LoanBookScopeToggleProps {
  mode: LoanBookMode;
  onModeChange: (mode: LoanBookMode) => void;
  /** Undefined means no primary entity is configured for this broker. */
  primaryEntity?: LoanBookEntity;
  disabled?: boolean;
}

export function primaryOptionLabel(primaryEntity?: LoanBookEntity): string {
  if (!primaryEntity) return 'Primary loan book not set';
  return `Primary loan book: ${primaryEntity.label} (${primaryEntity.code})`;
}

const BASE =
  'px-3 py-1.5 text-xs font-medium rounded-md transition-colors focus:outline-none focus:ring-1 focus:ring-blue-500/50';
const ACTIVE = 'bg-blue-600/25 text-blue-200 border border-blue-500/40';
const INACTIVE = 'text-gray-400 border border-transparent hover:text-white hover:bg-white/[0.05]';
const LOCKED = 'text-gray-600 border border-transparent cursor-not-allowed';

export const LoanBookScopeToggle: React.FC<LoanBookScopeToggleProps> = ({
  mode,
  onModeChange,
  primaryEntity,
  disabled = false,
}) => {
  const primaryAvailable = !!primaryEntity;
  const primaryLabel = primaryOptionLabel(primaryEntity);

  return (
    <div
      role="group"
      aria-label="Loan book entity scope"
      className="inline-flex items-center gap-1 bg-gray-900/50 border border-white/[0.06] rounded-lg p-1"
    >
      <button
        type="button"
        aria-pressed={mode === 'primary' && primaryAvailable}
        disabled={disabled || !primaryAvailable}
        title={
          primaryAvailable
            ? primaryLabel
            : 'No primary lending entity is configured for this broker, so every loan book is shown.'
        }
        onClick={() => primaryAvailable && onModeChange('primary')}
        className={`${BASE} ${
          !primaryAvailable ? LOCKED : mode === 'primary' ? ACTIVE : INACTIVE
        }`}
      >
        {primaryLabel}
      </button>
      <button
        type="button"
        aria-pressed={mode === 'all' || !primaryAvailable}
        disabled={disabled}
        onClick={() => onModeChange('all')}
        className={`${BASE} ${
          mode === 'all' || !primaryAvailable ? ACTIVE : INACTIVE
        }`}
      >
        All loan books
      </button>
    </div>
  );
};

export default LoanBookScopeToggle;
